import { useWebgate } from "@xvia/webgate-connect";
import { FC, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getRecommendedServices,
  getRelevant,
} from "../../apis/relevant-catalog";
import { Catalog } from "../../apis/search-catalog";
import { getServerData } from "../../util/get-server-data";
import { RelevantServices, RelevantServicesProps } from "./relevant-services";

export interface RelevantServicesContainerProps {
  allCatalogList?: Catalog[];
  favoriteList?: string[];
  onFavoriteUpdate?: (slug: string, remove: boolean) => void;
  setMessageAlert?: (message: string) => void;
  setSeverityAlert?: (severity: "success" | "error") => void;
  setOpenAlert?: (open: boolean) => void;
  codIbgeMunicipio?: number;
  municipalityName?: string;
  municipioQuerySuffix?: string;
  isMunicipalityHome?: boolean;
}

function initialRelevantList(
  codIbgeMunicipio: number | undefined,
  serverData: { relevant?: Catalog[] } | undefined
): Catalog[] {
  if (codIbgeMunicipio) {
    return [];
  }
  const raw = serverData?.relevant ?? getServerData("relevant", null);
  return Array.isArray(raw) ? raw : [];
}

export const RelevantServicesContainer: FC<RelevantServicesContainerProps> = ({
  favoriteList,
  onFavoriteUpdate,
  setMessageAlert,
  setSeverityAlert,
  setOpenAlert,
  codIbgeMunicipio,
  municipalityName,
  municipioQuerySuffix = "",
  isMunicipalityHome,
}) => {
  const { accessToken, serverData, userInfo } = useWebgate();
  let history = useHistory();

  const [relevantCatalogList, setRelevantCatalogList] = useState<Catalog[]>(
    () =>
      isMunicipalityHome && !codIbgeMunicipio
        ? []
        : initialRelevantList(codIbgeMunicipio, serverData)
  );

  const [hasFetched, setHasFetched] = useState(() => {
    if (isMunicipalityHome && !codIbgeMunicipio) {
      return true;
    }
    if (codIbgeMunicipio) {
      return false;
    }
    const seeded = initialRelevantList(undefined, serverData);
    return seeded.length > 0;
  });

  const getServerRelevant = useCallback(async () => {
    if (isMunicipalityHome && !codIbgeMunicipio) {
      setRelevantCatalogList([]);
      setHasFetched(true);
      return;
    }

    try {
      if (codIbgeMunicipio) {
        const regularServices = await getRelevant(
          accessToken,
          codIbgeMunicipio
        );
        setRelevantCatalogList(regularServices ?? []);
        return;
      }
      if (!userInfo || Object.keys(userInfo).length === 0) {
        const regularServices = await getRelevant(
          accessToken,
          codIbgeMunicipio
        );
        setRelevantCatalogList(regularServices ?? []);
        return;
      }
      const recommendedResult = await getRecommendedServices(
        userInfo?.preferred_username
      );
      if (recommendedResult.recommended_services?.length > 0) {
        setRelevantCatalogList(recommendedResult.recommended_services);
      } else {
        const regularServices = await getRelevant(
          accessToken,
          codIbgeMunicipio
        );
        setRelevantCatalogList(regularServices ?? []);
      }
    } catch {
      try {
        const regularServices = await getRelevant(
          accessToken,
          codIbgeMunicipio
        );
        setRelevantCatalogList(regularServices ?? []);
      } catch (fallbackError) {
        console.error("Error fetching fallback services:", fallbackError);
        setRelevantCatalogList([]);
      }
    } finally {
      setHasFetched(true);
    }
  }, [accessToken, codIbgeMunicipio, isMunicipalityHome, userInfo]);

  useEffect(() => {
    if (isMunicipalityHome && !codIbgeMunicipio) {
      setRelevantCatalogList([]);
      setHasFetched(true);
      return;
    }

    if (codIbgeMunicipio) {
      void getServerRelevant();
      return;
    }
    if (relevantCatalogList?.length) {
      setHasFetched(true);
    } else {
      void getServerRelevant();
    }
  }, [
    codIbgeMunicipio,
    getServerRelevant,
    isMunicipalityHome,
    relevantCatalogList?.length,
  ]);

  const handleFavoriteClick = useCallback(
    (slug: string, isFavorite: boolean) => {
      onFavoriteUpdate?.(slug, isFavorite);
    },
    [onFavoriteUpdate]
  );

  const onClickCard = useCallback(
    (catalog: Catalog) => {
      if (catalog.link.startsWith("http")) {
        globalThis.window.location.href = catalog.link;
      } else if (catalog.link.startsWith("/app/")) {
        globalThis.window.location.href = catalog.link;
      } else {
        history.push(
          `/${catalog.categorySlug}/${catalog.slug}${municipioQuerySuffix}`
        );
      }
    },
    [history, municipioQuerySuffix]
  );

  if (!hasFetched || !relevantCatalogList?.length) {
    return null;
  }

  const relevantServicesProps: RelevantServicesProps = {
    title: municipalityName
      ? `Acesso rápido - ${municipalityName}`
      : "Acesso rápido",
    linkUrl: `/app/catalog/search/acesso-rapido${municipioQuerySuffix}`,
    services: relevantCatalogList.map((catalog) => {
      const isFavorite = favoriteList?.includes(catalog.slug);
      return {
        title: catalog.title,
        description: catalog.description,
        categoryIcon: catalog.icon,
        onClick: () => onClickCard(catalog),
        imageUrl: catalog.imageUrl,
        type: catalog.isOnline ? "online" : "in-person",
        targets: catalog.targets,
        accessProfile: catalog.accessProfile,
        onClickFavorite: () =>
          handleFavoriteClick(catalog.slug, isFavorite ?? false),
        isFavorite,
        municipalityName: municipalityName,
        setSeverityAlert,
        setOpenAlert,
        slug: catalog.slug,
        setMessageAlert,
      };
    }),
  };

  return <RelevantServices {...relevantServicesProps} />;
};
